/** @jsxImportSource @emotion/react */
import React, { forwardRef, memo } from 'react';
import type {
  PaddingProps,
  MarginProps,
  StyleGroup,
  DimensionProps,
  PositioningProps,
  DisplayFlexProps,
  LayoutProps,
  OverflowFlexProps,
  BackgroundColorsProps,
  BoxMiscProps,
} from './helpers';
import { useStylesCss } from './helpers';
import { useTranslation } from '@vitvio/locales';

export interface CoreBoxProps
  extends PaddingProps,
    MarginProps,
    DimensionProps,
    PositioningProps,
    DisplayFlexProps,
    LayoutProps,
    OverflowFlexProps,
    BackgroundColorsProps,
    BoxMiscProps {
  children?: React.ReactNode;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  /** Marks this element as a group parent for `groupHoverDisplay` children. Renders a `data-group` attribute. */
  group?: boolean;
  role?: string;
  className?: string;
  // TODO: additional aria-* attributes can be added via a separate helper when needed
  ariaLabelPath?: string;
}

const BOX_STYLES: StyleGroup[] = [
  'display',
  'backgroundColors',
  'dimensions',
  'positioning',
  'padding',
  'margin',
  'layout',
  'overflow',
  'flex',
  'boxMisc',
  'scrollbar',
] as const;

export const CoreBox = memo(
  forwardRef<HTMLDivElement, CoreBoxProps>(
    (
      { children, onClick, onMouseEnter, onMouseLeave, onScroll, group, role, className, ariaLabelPath, ...styleProps },
      ref,
    ) => {
      const { t } = useTranslation();
      const effectiveCursor = styleProps.cursor ?? (onClick ? 'pointer' : undefined);
      const stylesCss = useStylesCss({ ...styleProps, cursor: effectiveCursor }, BOX_STYLES);
      const ariaLabel = ariaLabelPath ? t(ariaLabelPath) : undefined;

      return (
        <div
          ref={ref}
          data-group={group || undefined}
          role={role}
          className={className}
          aria-label={ariaLabel}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onScroll={onScroll}
          css={stylesCss}
        >
          {children}
        </div>
      );
    },
  ),
);

CoreBox.displayName = 'CoreBox';

export default CoreBox;
